import { useState } from "react";
import { Button, Table, Popconfirm } from "@/frontend/components/common";
import { tournamentFormatService } from "@/frontend/services";
import type { TournamentFormatResponseDTO, PaginationRequestDTO } from "@/shared/dto";
import { FaPlus, FaEdit, FaTrash } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import type { ColumnsType, TablePaginationConfig } from "antd/es/table";
import { usePagination, useDelete } from "@/frontend/hooks";
import { formatDate } from "@/shared/utils";
import { DatePicker } from "antd";

const { RangePicker } = DatePicker;

const initialPagination: TablePaginationConfig = {
  pageSizeOptions: ["7","10", "20", "50", "100"],
  showSizeChanger: true,
  showTotal: (total) => `Total: ${total} registros`,
};

const initialParams: PaginationRequestDTO = {
  page: 1,
  pageSize: 7,
};

export function TournamentFormatsListPage() {
  const navigate = useNavigate();

  const [, setDateRange] = useState<[string, string] | null>(null);

  const { data, loading, pagination, loadData } =
    usePagination<TournamentFormatResponseDTO>({
      fetchFn: tournamentFormatService.getPaginate,
      initialParams,
      initialPagination,
    });

  function applyFilters(range?: [string, string] | null) {
    loadData({ page: 1, pageSize: 7, createdFrom: range?.[0], createdTo: range?.[1] });
  }

  const { handleDelete, showConfirm, confirmTitle, confirmDescription } =
    useDelete({
      deleteFn: tournamentFormatService.delete,
      loadData,
      successMessage: "Evento de torneo eliminado exitosamente",
      errorMessage: "Error al eliminar evento de torneo",
      showConfirm: true,
      confirmTitle: "¿Eliminar evento de torneo?",
      confirmDescription:
        "Esta acción eliminará el evento permanentemente. ¿Estás seguro?",
    });

  const columns: ColumnsType<TournamentFormatResponseDTO> = [
    { title: "Nombre", dataIndex: "name", key: "name" },
    { title: "Deporte", dataIndex: "sport", key: "sport" },
    {
      title: "Fecha de Creación",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date: Date) => formatDate(date),
    },
    {
      title: "Acciones",
      key: "actions",
      width: 150,
      render: (_: any, record: TournamentFormatResponseDTO) => (
        <div className="flex gap-2">
          <Button
            type="link"
            icon={<FaEdit />}
            onClick={() => navigate(`/tournament-formats/edit/${record.id}`)}
          >
            Editar
          </Button>
          {showConfirm ? (
            <Popconfirm
              title={confirmTitle}
              description={confirmDescription}
              onConfirm={() => handleDelete(record.id)}
              okText="Sí, eliminar"
              cancelText="Cancelar"
              okButtonProps={{ danger: true }}
            >
              <Button type="link" danger icon={<FaTrash />}>
                Eliminar
              </Button>
            </Popconfirm>
          ) : (
            <Button
              type="link"
              danger
              icon={<FaTrash />}
              onClick={() => handleDelete(record.id)}
            >
              Eliminar
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="bg-white rounded-lg p-6 shadow-sm">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">
            Gestión de Eventos de Torneos
          </h2>
          <p className="text-gray-600 mt-1">
            Administra los eventos disponibles para los torneos
          </p>
        </div>
        <Button
          type="primary"
          icon={<FaPlus />}
          onClick={() => navigate("/tournament-formats/create")}
        >
          Crear Evento
        </Button>
      </div>

      <div className="flex gap-3 mb-4">
        <RangePicker
          placeholder={["Fecha inicio", "Fecha fin"]}
          onChange={(_, dateStrings) => {
            const range = dateStrings && dateStrings[0] && dateStrings[1]
              ? [dateStrings[0], dateStrings[1]] as [string, string]
              : null;
            setDateRange(range);
            applyFilters(range);
          }}
        />
      </div>

      <Table
        columns={columns}
        dataSource={data}
        loading={loading}
        rowKey="id"
        pagination={pagination}
      />
    </div>
  );
}
